/** Maps English short/long month names → Arabic when language is set to Arabic */
const ARABIC_MONTHS: Record<string, string> = {
  jan: "يناير", january: "يناير",
  feb: "فبراير", february: "فبراير",
  mar: "مارس", march: "مارس",
  apr: "أبريل", april: "أبريل",
  may: "مايو",
  jun: "يونيو", june: "يونيو",
  jul: "يوليو", july: "يوليو",
  aug: "أغسطس", august: "أغسطس",
  sep: "سبتمبر", september: "سبتمبر",
  oct: "أكتوبر", october: "أكتوبر",
  nov: "نوفمبر", november: "نوفمبر",
  dec: "ديسمبر", december: "ديسمبر",
};

const translateMonth = (monthStr: string): string => {
  if (typeof document !== "undefined" && document.documentElement.lang === "ar") {
    return ARABIC_MONTHS[monthStr.toLowerCase()] ?? monthStr;
  }
  return monthStr;
};

const formatDate = (isoDateString: string | null | undefined): string => {
  if (!isoDateString) return "";

  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const formatDateWithTime = (isoDateString: string | null | undefined): string => {
  if (!isoDateString) return "";

  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return "";

  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day} ${translateMonth(month)} ${year}, ${hours}:${minutes}`;
};

export const formatDateTime = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  // Day with suffix
  const day = date.getDate();
  const daySuffix = (d: number) => {
    if (d > 3 && d < 21) return "th"; // 4-20
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Time in hh:mm AM/PM
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  const isAr = typeof document !== "undefined" && document.documentElement.lang === "ar";
  if (isAr) {
    const arAmpm = ampm === "AM" ? "ص" : "م";
    return `${day} ${translateMonth(month)} ${year} في تمام الساعة ${hours}:${minutes} ${arAmpm}`;
  }

  return `${day}${daySuffix(day)} ${translateMonth(month)} ${year} at ${hours}:${minutes} ${ampm}`;
};
export const formatDateTimeNew = (isoDateString: string): string => {
  if (!isoDateString) return "";

  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");

  return `${day}-${month}-${year} : ${formattedHours}:${minutes} ${ampm}`;
};

// utils/dateTime.ts

// 👉 Format date like "27th Aug 2021"
export const formatDateNewFormat = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  const day = date.getDate();
  const daySuffix = (d: number) => {
    if (d > 3 && d < 21) return "th"; // 4-20
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  const isAr = typeof document !== "undefined" && document.documentElement.lang === "ar";
  if (isAr) {
    return `${day} ${translateMonth(month)} ${year}`;
  }

  return `${day}${daySuffix(day)} ${translateMonth(month)} ${year}`;
};

// 👉 Format time like "At 2:30 PM"
export const formatTime = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // 0 → 12

  const isAr = typeof document !== "undefined" && document.documentElement.lang === "ar";
  if (isAr) {
    const arAmpm = ampm === "AM" ? "ص" : "م";
    return `في تمام الساعة ${hours}:${minutes} ${arAmpm}`;
  }

  return `At ${hours}:${minutes} ${ampm}`;
};

// Add this utility function near your other imports
export const openPdfInNewTab = (base64Data: string) => {
  try {
    // Decode base64 to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the binary data
    const blob = new Blob([bytes], { type: "application/pdf" });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Open in new tab
    window.open(url, "_blank");

    // Clean up after some time (optional)
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error("Error opening PDF:", error);
    alert("Failed to open the PDF document");
  }
};

export function maskedSecretKey(key: string) {
  const maskedCode = key?.toString()?.slice(0, 6);
  const formattedCode = maskedCode
    ? `${maskedCode[0]}**********${maskedCode.slice(-1)}`
    : "";
  return formattedCode;
}

export const getFileNameFromUrl = (url: string): string => {
  return decodeURIComponent(url?.split("/")?.pop()?.split("?")[0] || "");
};

export function getFileExtension(url: any): string {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split(".").pop()?.toLowerCase() || "";
  } catch (error) {
    return ""; // Return empty string if input is not a valid URL
  }
}

export default formatDate;

export const timezoneCities: Record<string, string> = {
  "Asia/Kolkata": "Chennai, Kolkata, Mumbai, New Delhi, Bangalore, Hyderabad",
  "Asia/Dubai": "Dubai, Abu Dhabi, Sharjah",
  "Asia/Riyadh": "Riyadh, Jeddah, Dammam, Mecca",
  "Asia/Singapore": "Singapore, Kuala Lumpur",
  "Asia/Tokyo": "Tokyo, Osaka, Yokohama",
  "Asia/Shanghai": "Shanghai, Beijing, Tianjin, Chongqing",
  "Asia/Seoul": "Seoul, Busan, Incheon",
  "Asia/Bangkok": "Bangkok, Hanoi, Phnom Penh",
  "Asia/Jakarta": "Jakarta, Bandung, Surabaya",
  "Asia/Manila": "Manila, Quezon City, Davao",
  "Asia/Ho_Chi_Minh": "Ho Chi Minh City, Hanoi, Da Nang",
  "Asia/Dhaka": "Dhaka, Chittagong, Khulna",
  "Asia/Karachi": "Karachi, Lahore, Islamabad",
  "Asia/Colombo": "Colombo, Kandy, Galle",
  "Asia/Kathmandu": "Kathmandu, Pokhara, Patan",
  "Asia/Yangon": "Yangon, Mandalay, Naypyidaw",
  "Asia/Tashkent": "Tashkent, Samarkand, Bukhara",
  "Asia/Almaty": "Almaty, Nur-Sultan, Shymkent",
  "Asia/Baku": "Baku, Ganja, Sumqayit",
  "Asia/Yerevan": "Yerevan, Gyumri, Vanadzor",
  "Asia/Tbilisi": "Tbilisi, Batumi, Kutaisi",
  "Asia/Tehran": "Tehran, Mashhad, Isfahan",
  "Asia/Baghdad": "Baghdad, Basra, Mosul",
  "Asia/Kuwait": "Kuwait City, Al Ahmadi, Hawalli",
  "Asia/Qatar": "Doha, Al Rayyan, Umm Salal",
  "Asia/Bahrain": "Manama, Riffa, Muharraq",
  "Asia/Muscat": "Muscat, Seeb, Salalah",
  "Asia/Aden": "Aden, Sana'a, Taiz",
  "Asia/Amman": "Amman, Zarqa, Irbid",
  "Asia/Beirut": "Beirut, Tripoli, Sidon",
  "Asia/Damascus": "Damascus, Aleppo, Homs",
  "Asia/Jerusalem": "Jerusalem, Tel Aviv, Haifa",
  "Asia/Gaza": "Gaza City, Khan Yunis, Rafah",
  "Asia/Hebron": "Hebron, Bethlehem, Nablus",
  "Asia/Nicosia": "Nicosia, Limassol, Larnaca",
  "Asia/Anadyr": "Anadyr, Bilibino, Pevek",
  "Asia/Kamchatka": "Petropavlovsk-Kamchatsky, Yelizovo, Vilyuchinsk",
  "Asia/Magadan": "Magadan, Susuman, Ola",
  "Asia/Sakhalin": "Yuzhno-Sakhalinsk, Korsakov, Kholmsk",
  "Asia/Vladivostok": "Vladivostok, Khabarovsk, Ussuriysk",
  "Asia/Yakutsk": "Yakutsk, Mirny, Neryungri",
  "Asia/Irkutsk": "Irkutsk, Bratsk, Angarsk",
  "Asia/Krasnoyarsk": "Krasnoyarsk, Norilsk, Achinsk",
  "Asia/Novosibirsk": "Novosibirsk, Omsk, Tomsk",
  "Asia/Omsk": "Omsk, Tara, Kalachinsk",
  "Asia/Yekaterinburg": "Yekaterinburg, Chelyabinsk, Tyumen",
  "Asia/Samara": "Samara, Tolyatti, Syzran",
  "Asia/Ulyanovsk": "Ulyanovsk, Dimitrovgrad, Barysh",
  "Europe/Moscow": "Moscow, Saint Petersburg, Novosibirsk",
  "Europe/Kaliningrad": "Kaliningrad, Sovetsk, Chernyakhovsk",
  "Europe/Kiev": "Kyiv, Kharkiv, Odesa",
  "Europe/Minsk": "Minsk, Gomel, Mogilev",
  "Europe/Chisinau": "Chisinau, Tiraspol, Balti",
  "Europe/Bucharest": "Bucharest, Cluj-Napoca, Timișoara",
  "Europe/Sofia": "Sofia, Plovdiv, Varna",
  "Europe/Athens": "Athens, Thessaloniki, Patras",
  "Europe/Istanbul": "Istanbul, Ankara, Izmir",
  "Europe/Helsinki": "Helsinki, Espoo, Tampere",
  "Europe/Tallinn": "Tallinn, Tartu, Narva",
  "Europe/Riga": "Riga, Daugavpils, Liepāja",
  "Europe/Vilnius": "Vilnius, Kaunas, Klaipėda",
  "Europe/Warsaw": "Warsaw, Kraków, Łódź",
  "Europe/Prague": "Prague, Brno, Ostrava",
  "Europe/Budapest": "Budapest, Debrecen, Szeged",
  "Europe/Bratislava": "Bratislava, Košice, Prešov",
  "Europe/Vienna": "Vienna, Graz, Linz",
  "Europe/Berlin": "Berlin, Hamburg, Munich",
  "Europe/Paris": "Paris, Marseille, Lyon",
  "Europe/Brussels": "Brussels, Antwerp, Ghent",
  "Europe/Amsterdam": "Amsterdam, Rotterdam, The Hague",
  "Europe/Luxembourg": "Luxembourg City, Esch-sur-Alzette, Differdange",
  "Europe/Zurich": "Zurich, Geneva, Basel",
  "Europe/Rome": "Rome, Milan, Naples",
  "Europe/Madrid": "Madrid, Barcelona, Valencia",
  "Europe/Lisbon": "Lisbon, Porto, Braga",
  "Europe/London": "London, Birmingham, Manchester",
  "Europe/Dublin": "Dublin, Cork, Limerick",
  "Europe/Stockholm": "Stockholm, Gothenburg, Malmö",
  "Europe/Oslo": "Oslo, Bergen, Trondheim",
  "Europe/Copenhagen": "Copenhagen, Aarhus, Odense",
  "Africa/Cairo": "Cairo, Alexandria, Giza",
  "Africa/Johannesburg": "Johannesburg, Cape Town, Durban",
  "Africa/Lagos": "Lagos, Kano, Ibadan",
  "Africa/Nairobi": "Nairobi, Mombasa, Kisumu",
  "Africa/Addis_Ababa": "Addis Ababa, Dire Dawa, Mekele",
  "Africa/Dar_es_Salaam": "Dar es Salaam, Mwanza, Zanzibar",
  "Africa/Khartoum": "Khartoum, Omdurman, Port Sudan",
  "Africa/Algiers": "Algiers, Oran, Constantine",
  "Africa/Casablanca": "Casablanca, Rabat, Marrakesh",
  "Africa/Tunis": "Tunis, Sfax, Sousse",
  "Africa/Tripoli": "Tripoli, Benghazi, Misrata",
  "America/New_York": "New York, Philadelphia, Boston",
  "America/Chicago": "Chicago, Houston, Dallas",
  "America/Denver": "Denver, Phoenix, Salt Lake City",
  "America/Los_Angeles": "Los Angeles, San Francisco, San Diego",
  "America/Toronto": "Toronto, Montreal, Vancouver",
  "America/Mexico_City": "Mexico City, Guadalajara, Monterrey",
  "America/Sao_Paulo": "Sao Paulo, Rio de Janeiro, Brasilia",
  "America/Buenos_Aires": "Buenos Aires, Cordoba, Rosario",
  "America/Lima": "Lima, Arequipa, Trujillo",
  "America/Bogota": "Bogota, Medellin, Cali",
  "America/Caracas": "Caracas, Maracaibo, Valencia",
  "America/Santiago": "Santiago, Valparaiso, Concepcion",
  "Australia/Sydney": "Sydney, Melbourne, Brisbane",
  "Australia/Perth": "Perth, Fremantle, Mandurah",
  "Australia/Adelaide": "Adelaide, Mount Gambier, Whyalla",
  "Australia/Darwin": "Darwin, Palmerston, Alice Springs",
  "Pacific/Auckland": "Auckland, Wellington, Christchurch",
  "Pacific/Fiji": "Suva, Lautoka, Nadi",
  "Pacific/Guam": "Hagåtña, Dededo, Tamuning",
  "Pacific/Honolulu": "Honolulu, Pearl City, Hilo",
};

export const sanitizePayload = <T extends Record<string, any>>(
  payload: T
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(payload).filter(([_, v]) => {
      if (v === undefined || v === null) return false;
      if (typeof v === "string" && v.trim() === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
  ) as Partial<T>;
};

export const isSameDay = (
  date1: Date | string,
  date2: Date | string = new Date()
): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

// const getFileUrl = (url?: string) => {
//   if (!url) return "";
//   if (url.startsWith("http")) return url;
//   return FILE_BASE_URL + url;
// };


export const forceDownloadImage = (url: string, filename: string) => {
 const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
